<template>
  <b-modal
    v-model="showModal"
    title="Add Buyer Ticket"
    size="xl"
    no-close-on-backdrop
    @hidden="$emit('modal-closed')"
    @ok="submit"
  >
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <div v-if="!loading && !loadingError">

      <b-form
        @submit.prevent="submit"
      >
        <validation-observer ref="buyerTicketForm">

          <b-row>
            <b-col
              md="6"
            >
              <validation-provider
                #default="{ errors }"
                name="Buyer"
                :vid="'buyer'"
                rules="required"
              >
                <b-form-group
                  label="Buyer"
                  :state="errors.length > 0 ? false : null"
                >
                  <custom-v-select
                    v-model="buyerId"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="buyerOptions"
                    label="text"
                    placeholder="Select Buyer"
                    :state="errors.length > 0 ? false:null"
                    :reduce="option => option.id"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col md="6">
              <validation-provider
                #default="{ errors }"
                name="Departments"
                vid="departments"
                rules="required"
              >
                <b-form-group
                  label="Departments"
                  :state="errors.length > 0 ? false : null"
                >
                  <custom-v-select
                    v-model="departments"
                    multiple
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="displayDepartmentOptions"
                    label="text"
                    placeholder="Select Departments"
                    :clearable="false"
                    :reduce="option => option.id"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Basket Number">
                <div class="d-flex align-items-center">
                  <b-form-select
                    v-model="basketType"
                    :options="basketTypeOptions"
                    style="flex: 1;"
                    @change="onBasketTypeChange"
                  >
                    <template #first>
                      <b-form-select-option :value="null">
                        Select Type
                      </b-form-select-option>
                    </template>
                  </b-form-select>

                  <span class="mx-2">-</span>

                  <validation-provider
                    #default="{ errors }"
                    name="Basket Number"
                    vid="basket_number"
                    :rules="basketType ? 'required' : ''"
                    style="flex: 1;"
                  >
                    <b-form-select
                      v-if="basketType && basketType !== 'CARTOON'"
                      v-model="basketNumber"
                      :options="basketNumberOptions"
                      :state="errors.length > 0 ? false : null"
                      style="width: 100%;"
                    >
                      <template #first>
                        <b-form-select-option :value="null">
                          Select Number
                        </b-form-select-option>
                      </template>
                    </b-form-select>
                    <b-form-input
                      v-else-if="basketType === 'CARTOON'"
                      v-model="basketNumber"
                      type="number"
                      placeholder="Enter number"
                      :state="errors.length > 0 ? false : null"
                      style="width: 100%;"
                    />
                    <b-form-input
                      v-else
                      value=""
                      placeholder="Select type first"
                      disabled
                      style="width: 100%;"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <ticket-discrepancies
            v-model="discrepancies"
            :item-options="itemOptions"
            :party-options="buyerOptions"
            is-buyer-ticket
          />
        </validation-observer>
      </b-form>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="saving || loading || loadingError ? true : false"
        @click="ok()"
      >
        Submit
        <b-spinner
          v-if="saving"
          small
          label="Small Spinner"
        />
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BRow, BCol, BFormGroup, BForm, BSpinner, BAlert, BButton,
  BFormInput, BFormSelect, BFormSelectOption,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import TicketDiscrepancies from '@/components/Tickets/TicketDiscrepancy/TicketDiscrepancies.vue'

export default {
  components: {
    CustomVSelect,
    BModal,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormSelectOption,
    BForm,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    BAlert,
    TicketDiscrepancies,
  },
  data() {
    return {
      loading: false,
      loadingError: null,
      showModal: true,
      buyerId: null,
      buyerOptions: [],
      departments: [],
      departmentOptions: [],
      saving: false,
      itemOptions: [],
      discrepancies: [],
      basketType: null,
      basketNumber: null,
      basketTypeOptions: [
        { value: 'EXP', text: 'EXP' },
        { value: 'SR', text: 'SR' },
        { value: 'CARTOON', text: 'CARTOON' },
      ],
    }
  },
  computed: {
    displayDepartmentOptions() {
      return this.departmentOptions.map(option => ({
        id: option,
        text: option.charAt(0).toUpperCase() + option.slice(1),
      }))
    },
    basketNumberOptions() {
      if (this.basketType === 'EXP') {
        return Array.from({ length: 30 }, (_, i) => i + 1)
      }
      if (this.basketType === 'SR') {
        return Array.from({ length: 20 }, (_, i) => i + 1)
      }
      return []
    },
    basket_no() {
      if (!this.basketType || !this.basketNumber) return ''
      return `${this.basketType}-${this.basketNumber}`
    },
  },
  created() {
    this.initializeForm()
  },
  methods: {
    onBasketTypeChange() {
      this.basketNumber = null
    },
    async submit(event) {
      event.preventDefault()

      const isFormValid = await this.$refs.buyerTicketForm.validate()

      if (!isFormValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Please correct the form errors',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      if (!this.discrepancies.length) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atleast one discrepancy is required to create a ticket.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return
      }

      this.saving = true
      const formData = {
        buyer: this.buyerId,
        departments: this.departments,
        basket_no: this.basket_no,
        buyer_discrepancies: this.discrepancies.map(item => ({
          type: item.type,
          details: item.details,
          buyer_discrepancy_remarks: item.discrepancy_remarks.map(remarks => ({
            ...remarks,
          })),
        })),
      }

      try {
        await axios.post('/tickets/buyer-ticket/', formData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Ticket created successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$refs.buyerTicketForm.reset()
        this.saving = false
        this.showModal = false
        this.$emit('saved')
      } catch (error) {
        this.$refs.buyerTicketForm.reset()

        const statusCode = error?.response?.status
        const serverErrors = error?.response?.data
        if (statusCode === 400 && serverErrors) {
          this.$refs.buyerTicketForm.setErrors(serverErrors)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Please correct the form errors',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.detail || 'Error saving ticket',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        }
        this.saving = false
      } finally {
        this.saving = false
      }
    },
    async initializeForm() {
      this.loading = true
      try {
        const res = await axios.get('/accounts/buyers_options')
        this.buyerOptions = res.data.data
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Error fetching buyer options'
        this.loading = false
      }

      try {
        const res = await axios.get('/item_options')
        this.itemOptions = res.data.data
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Error fetching item options'
        this.loading = false
      }

      try {
        const res = await axios.get('/tickets/department_options')
        this.departmentOptions = res.data.departments
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Error fetching department options'
      }
      this.loading = false
    },

  },
}
</script>
